import type { Swiper as SwiperInstance } from 'swiper'

export type PanelNavigationDirection = 'next' | 'prev'

interface NavigablePanel {
    empty?: boolean
}

const feedbackAnimations = new WeakMap<HTMLElement, Animation>()

export function hasPanelInDirection(
    panels: ReadonlyArray<NavigablePanel | null | undefined>,
    currentIndex: number,
    direction: PanelNavigationDirection,
) {
    const targetIndex = currentIndex + (direction === 'next' ? 1 : -1)
    const targetPanel = panels[targetIndex]
    return Boolean(targetPanel && !targetPanel.empty)
}

export function playPanelBoundaryFeedback(
    swiper: SwiperInstance | null | undefined,
    direction: PanelNavigationDirection,
) {
    if (!swiper || swiper.destroyed) return false

    const wrapper = swiper.wrapperEl
    if (!wrapper || typeof wrapper.animate !== 'function') return false

    feedbackAnimations.get(wrapper)?.cancel()

    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    const distance = (reducedMotion ? 16 : 32) * (direction === 'next' ? -1 : 1)
    const duration = reducedMotion ? 160 : 320
    const restingTransform = window.getComputedStyle(wrapper).transform
    const baseTransform = restingTransform === 'none' ? 'translate3d(0, 0, 0)' : restingTransform
    const animation = wrapper.animate(
        [
            { transform: baseTransform },
            {
                offset: 0.35,
                transform: `${baseTransform} translate3d(${distance}px, 0, 0)`,
            },
            { transform: baseTransform },
        ],
        {
            duration,
            easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
        },
    )

    feedbackAnimations.set(wrapper, animation)
    wrapper.dataset.panelBoundaryFeedback = direction

    const clearFeedbackState = () => {
        if (feedbackAnimations.get(wrapper) !== animation) return
        feedbackAnimations.delete(wrapper)
        delete wrapper.dataset.panelBoundaryFeedback
    }
    animation.onfinish = clearFeedbackState
    animation.oncancel = clearFeedbackState

    return true
}
